import React, { Component, Fragment } from 'react';
import { Table, Card, message, Button, Tooltip as TT, Tabs, Modal, Row, Col, Spin } from 'antd';
import { inject, observer } from 'mobx-react/index';
import moment from 'moment/moment';
import { AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Area } from 'recharts';
const TabPane = Tabs.TabPane;
const usdIds = ['USDT', 'PAX', 'TUSD', 'USDC', 'USD'];

@inject('store')
@observer
export class PerformanceView extends Component {
    constructor(props) {
        super(props);
        this.state = {};
    }

    async componentDidMount() {
        this.props.store.botStore.getDailyPerformance();
        this.props.store.botStore.getCoinPerformance();
        this.props.store.botStore.getPortfolioHistory();
    }

    prettyDuration(value) {
        const days = Math.floor(value / 86400000);
        value = value % 86400000;
        const hours = Math.floor(value / 3600000);
        value = value % 3600000;
        const minutes = Math.floor(value / 60000);
        value = value % 60000;
        const seconds = Math.floor(value / 1000);
        return (
            (days ? days + ' days ' : '') +
            (hours ? hours + ' hours ' : '') +
            (minutes ? minutes + ' minutes ' : '') +
            (seconds ? seconds + ' seconds ' : '')
        );
    }

    render() {
        //Balance Info
        const balances = this.props.store.botStore.balanceStore.balances;
        const prices = this.props.store.botStore.marketStore.market;
        const botConfig = this.props.store.botStore.currentConfiguration;

        if (!Object.keys(prices).length)
            return (
                <div style={{ padding: 50, textAlign: 'center' }}>
                    <Spin tip="Loading" size="large" />
                </div>
            );

        const augmentedBalances = [];
        let btcTotal = 0;
        let baseTotal = null;
        let usdtTotal = 0;
        const usdId = prices[`BTC/USD`] ? 'USD' : 'USDT';
        const nonUsdBase = !usdIds.includes(botConfig.base_currency);

        balances.forEach(symbol => {
            const priceInfo =
                symbol.id === usdId ? prices[`BTC/${symbol.id}`] : prices[`${symbol.id}/BTC`];
            const baseInfo = prices[`${botConfig.base_currency}/BTC`];
            const btcDollar = prices[`BTC/${usdId}`].last;

            if ((priceInfo && symbol.total > 0) || symbol.id === 'BTC') {
                let btcValue = symbol.id === 'BTC' ? symbol.total : symbol.total * priceInfo.last;
                if (symbol.id === usdId) {
                    btcValue = symbol.total / priceInfo.last;
                }

                btcTotal += btcValue;
                usdtTotal += btcValue * btcDollar;

                if (baseInfo) {
                    baseTotal += btcValue / baseInfo.last;
                }

                augmentedBalances.push({
                    ...symbol,
                    btc_value: btcValue,
                    dollar_value: btcValue * btcDollar,
                    base: baseInfo ? baseInfo.name.replace('BTC', '') : '',
                    base_value: baseInfo ? btcValue / baseInfo.last : null
                });
            }
        });

        //Coin Perf
        const coinPerf = this.props.store.botStore.coinPerformance.reduce((acc, record) => {
            if (!acc[record.base]) acc[record.base] = {};

            Object.keys(record.coins).forEach(symbol => {
                if (!acc[record.base][symbol]) {
                    acc[record.base][symbol] = { symbol: symbol, ...record.coins[symbol] };
                    return;
                }

                acc[record.base][symbol].won += record.coins[symbol].won;
                acc[record.base][symbol].lost += record.coins[symbol].lost;
                acc[record.base][symbol].hold_time += record.coins[symbol].hold_time;
                acc[record.base][symbol].profit_per_trade += record.coins[symbol].profit_per_trade;
                acc[record.base][symbol].profit_total += record.coins[symbol].profit_total;
            });

            return acc;
        }, {});

        //Todays Profit

        const todaysRec = this.props.store.botStore.dailyPerformance[0];
        let totalProfit = 0;
        let totalProfitInDollar = 0;

        if (todaysRec) {
            const today = new Date();
            const firstRec = new Date(todaysRec.day);

            if (
                today.getDate() === firstRec.getDate() &&
                today.getMonth() === firstRec.getMonth()
            ) {
                totalProfit = todaysRec.profit_total.toFixed(8);

                if (nonUsdBase) {
                    const baseToDollar = prices[`${botConfig.base_currency}/${usdId}`].last;
                    totalProfitInDollar = totalProfit * baseToDollar;
                }
            }
        }

        //Portfolio Growth
        const first = this.props.store.botStore.portfolioHistory[0];
        const lastGrowth = this.props.store.botStore.portfolioHistory[
            this.props.store.botStore.portfolioHistory.length - 1
        ];
        let totalGrowth = 0;
        let totalGrowthInDollar = 0;
        let growthInDays = 1;
        let dailyPPP = 0;
        let portfolioP = 0;
        if (first && lastGrowth) {
            const baseStart = first.estimated_totals[botConfig.base_currency];
            const baseLast = lastGrowth.estimated_totals[botConfig.base_currency];

            if (baseStart && baseLast) {
                totalGrowth = baseLast - baseStart;
                if (nonUsdBase) {
                    const baseToDollar = prices[`${botConfig.base_currency}/${usdId}`].last;
                    totalGrowthInDollar = totalGrowth * baseToDollar;
                }

                let now = moment(first.created_at); //todays date
                let end = moment(baseLast.created_at); // another date
                let duration = moment.duration(end.diff(now));
                growthInDays = duration.asDays().toFixed(0);
                portfolioP = (baseLast - baseStart) / baseStart;
                dailyPPP = (portfolioP / (!parseInt(growthInDays, 10) ? 1 : growthInDays)) * 100;
            }
        }

        return (
            <div>
                <Row gutter={24} style={{ marginBottom: 15 }}>
                    <Col xs={24} md={6}>
                        <Card style={{ height: 145 }} className="primary widget" bordered={false}>
                            <div className="heading">Account Value</div>
                            <div className={'content'}>
                                {!baseTotal
                                    ? `${btcTotal.toFixed(7)} BTC \n `
                                    : `${baseTotal.toFixed(8)} ${botConfig.base_currency} \n`}
                            </div>
                            <div>${usdtTotal.toFixed(2)}</div>
                        </Card>
                    </Col>
                    <Col xs={24} md={6}>
                        <Card style={{ height: 145 }} className="dark widget" bordered={false}>
                            <div className="heading">Today's Profit</div>
                            <div className={'content'}>
                                {totalProfit} {botConfig.base_currency}
                            </div>
                            <div>${totalProfitInDollar.toFixed(2)}</div>
                        </Card>
                    </Col>
                    <Col xs={24} md={6}>
                        <Card style={{ height: 145 }} className="primary widget" bordered={false}>
                            <div className="heading">Portfolio Growth - Daily</div>
                            <div className={'content'}>
                                {(
                                    totalGrowth / (!parseInt(growthInDays, 10) ? 1 : growthInDays)
                                ).toFixed(8)}{' '}
                                {botConfig.base_currency}
                            </div>
                            <div>
                                {(dailyPPP || 0).toFixed(2)}% &nbsp;&nbsp;{' '}
                                {totalGrowthInDollar && growthInDays
                                    ? `$${(
                                          totalGrowthInDollar /
                                          (!parseInt(growthInDays, 10) ? 1 : growthInDays)
                                      ).toFixed(2)}`
                                    : ''}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={6}>
                        <Card style={{ height: 145 }} className="dark widget" bordered={false}>
                            <div className="heading">
                                Portfolio Growth (Last {growthInDays} Days)
                            </div>
                            <div className={'content'}>
                                {totalGrowth.toFixed(8)} {botConfig.base_currency}
                            </div>
                            <div>
                                {(portfolioP * 100).toFixed(2)}% &nbsp;&nbsp;{' '}
                                {totalGrowthInDollar ? `\n $${totalGrowthInDollar.toFixed(2)}` : ''}
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Card
                    bordered={false}
                    title={`Last ${growthInDays} Days`}
                    extra={
                        <Fragment>
                            <Button
                                onClick={() => {
                                    message.success('Stats will regenerate shortly.');
                                    this.props.store.botStore.actionStore.execute(
                                        'regenerate_performance_stats'
                                    );
                                }}
                            >
                                Regenerate
                            </Button>
                            &nbsp;&nbsp;
                            <Button
                                onClick={async () => {
                                    Modal.confirm({
                                        title: 'Reset All Stats',
                                        content: 'All data from the last 30 days will be deleted.',
                                        onOk: async () => {
                                            message.success('Stats cleared');
                                            await this.props.store.botStore.clearDailyHistory();
                                            this.props.store.botStore.actionStore.execute(
                                                'regenerate_performance_stats'
                                            );
                                        }
                                    });
                                }}
                            >
                                Clear
                            </Button>
                        </Fragment>
                    }
                >
                    <Table
                        scroll={{ x: 1000 }}
                        size="small"
                        rowKey="day"
                        pagination={false}
                        dataSource={this.props.store.botStore.dailyPerformance}
                        columns={[
                            {
                                title: 'Base',
                                dataIndex: 'base',
                                key: 'base',
                                defaultSortOrder: 'descend'
                            },
                            {
                                title: 'Day',
                                dataIndex: 'day',
                                key: 'day',
                                defaultSortOrder: 'descend',
                                sorter: (a, b) => {
                                    return a.date - b.date;
                                },
                                render: val => {
                                    return moment.utc(val).format('DD MMM');
                                }
                            },
                            {
                                title: 'Invested',
                                dataIndex: 'invested',
                                key: 'invested',
                                render: text => text.toFixed(8)
                            },
                            {
                                title: 'Received',
                                dataIndex: 'received',
                                key: 'received',
                                render: text => text.toFixed(8)
                            },
                            {
                                title: 'Dust',
                                dataIndex: 'dust',
                                key: 'dust',
                                render: text => (text || 0).toFixed(8)
                            },
                            {
                                title: 'Fees',
                                dataIndex: 'fees',
                                key: 'fees',
                                render: text => text.toFixed(8)
                            },
                            {
                                title: 'Avg Profit per Trade',
                                dataIndex: 'profit_per_trade',
                                key: 'profit_per_trade',
                                render: (text, r) => {
                                    return (
                                        <span className={text >= 0 ? 'success' : 'error'}>
                                            {text.toFixed(8)} (
                                            {(
                                                ((r.dust + r.received - r.fees - r.invested) /
                                                    r.invested) *
                                                100
                                            ).toFixed(2)}
                                            %)
                                        </span>
                                    );
                                }
                            },
                            {
                                title: 'Total Profit',
                                dataIndex: 'profit_total',
                                key: 'profit_total',
                                render: (text, r) => {
                                    const portfolioEntry = this.props.store.botStore.portfolioHistory.filter(
                                        a => a.created_at >= r.day && a.base_currency === r.base
                                    )[0];
                                    if (portfolioEntry) {
                                        return (
                                            <TT
                                                title={`Based on allocation: ${
                                                    portfolioEntry.total_allocation
                                                } at ${moment(portfolioEntry.created_at).format(
                                                    'HHa'
                                                )}`}
                                            >
                                                <span className={text >= 0 ? 'success' : 'error'}>
                                                    {text.toFixed(8)} (
                                                    {(
                                                        (text / portfolioEntry.total_allocation) *
                                                        100
                                                    ).toFixed(2)}{' '}
                                                    %)
                                                </span>
                                            </TT>
                                        );
                                    }
                                    return (
                                        <span className={text >= 0 ? 'success' : 'error'}>
                                            {text.toFixed(8)}
                                        </span>
                                    );
                                }
                            },
                            {
                                title: 'Won',
                                dataIndex: 'won',
                                key: 'won'
                            },
                            {
                                title: 'Lost',
                                dataIndex: 'lost',
                                key: 'lost'
                            },
                            {
                                title: 'Avg Hold Time',
                                dataIndex: 'hold_time_avg_pretty',
                                key: 'hold_time_avg_pretty'
                            }
                        ]}
                    />
                </Card>

                <Card title={'Coin Performance'} style={{ marginTop: 15 }} bordered={false}>
                    <Tabs>
                        {Object.keys(coinPerf).map(key => {
                            return (
                                <TabPane tab={key} key={key}>
                                    <Table
                                        scroll={{ x: 1000 }}
                                        size="small"
                                        rowKey="symbol"
                                        dataSource={Object.values(coinPerf[key])}
                                        columns={[
                                            {
                                                title: 'Symbol',
                                                dataIndex: 'symbol',
                                                key: 'symbol'
                                            },
                                            {
                                                title: 'Total Profit',
                                                dataIndex: 'profit_total',
                                                key: 'profit_total',
                                                defaultSortOrder: 'descend',
                                                sorter: (a, b) => {
                                                    return a.profit_total - b.profit_total;
                                                },
                                                render: text => text.toFixed(8)
                                            },
                                            {
                                                title: 'Won',
                                                dataIndex: 'won',
                                                key: 'won',
                                                sorter: (a, b) => {
                                                    return a.won - b.won;
                                                }
                                            },
                                            {
                                                title: 'Lost',
                                                dataIndex: 'lost',
                                                key: 'lost',
                                                sorter: (a, b) => {
                                                    return a.lost - b.lost;
                                                }
                                            },
                                            {
                                                title: 'Avg Hold Time',
                                                dataIndex: 'hold_time',
                                                key: 'hold_time',
                                                sorter: (a, b) => {
                                                    return a.hold_time - b.hold_time;
                                                },
                                                render: text => {
                                                    return this.prettyDuration(text);
                                                }
                                            }
                                        ]}
                                    />
                                </TabPane>
                            );
                        })}
                    </Tabs>
                </Card>

                <Card
                    title={'Balance History'}
                    bordered={false}
                    style={{ marginTop: 15 }}
                    extra={
                        <Button onClick={() => this.props.store.botStore.clearPortfolioHistory()}>
                            Reset
                        </Button>
                    }
                >
                    <ResponsiveContainer height={300} width="100%">
                        <AreaChart data={this.props.store.botStore.portfolioHistory}>
                            <XAxis
                                dataKey="created_at"
                                domain={['auto', 'auto']}
                                tickFormatter={ut => {
                                    return moment(ut).format('DD/MM HHa');
                                }}
                            />
                            <YAxis width={40} />
                            <Area
                                type="monotone"
                                dataKey={`estimated_totals[${botConfig.base_currency}]`}
                                stroke="#7954ff"
                                fillOpacity={0.3}
                                fill="#7954ff"
                            />

                            <Tooltip
                                content={options => {
                                    if (!options.payload || !options.payload[0]) return;

                                    return (
                                        <div style={{ background: '#323945', padding: 20 }}>
                                            {moment(options.label).format('ddd HHa')}

                                            <div>
                                                Portfolio Value ({botConfig.base_currency}):{' '}
                                                {options && options.payload[0].value}
                                            </div>
                                            <div>
                                                Base Total (
                                                {options.payload[1] &&
                                                    options.payload[1].payload.base_currency}
                                                ): {options.payload[1] && options.payload[1].value}
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/Account/Bot/PerformanceView/index.js